import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { InterviewManagementFacade } from '@/services/interview';

// Valid tags that match the database enum
const VALID_TAGS = [
  "frontend", 
  "backend", 
  "fullstack", 
  "devops", 
  "mobile", 
  "ai", 
  "data", 
  "security", 
  "leadership", 
  "architecture"
];

export const useProductionForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [context, setContext] = useState('');
  const [contextUrl, setContextUrl] = useState('');
  const [contextImageUrl, setContextImageUrl] = useState('');
  const [contextImageCaption, setContextImageCaption] = useState('');
  const [question, setQuestion] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photographerCredit, setPhotographerCredit] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isSponsored, setIsSponsored] = useState(false);
  const [sponsorCompany, setSponsorCompany] = useState('');
  const [sponsorLogoUrl, setSponsorLogoUrl] = useState('');
  const [sponsorLogoPosition, setSponsorLogoPosition] = useState<'profile' | 'article' | 'both'>('profile');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!answer.trim() || !name.trim() || !question.trim() || !context.trim()) {
      toast.error('Vennligst fyll ut alle påkrevde felt');
      return;
    }

    // Filter valid tags
    const validTags = tags.filter(tag => VALID_TAGS.includes(tag));
    if (validTags.length < tags.length) {
      toast.warning('Noen av emneordene er ikke gyldige og vil bli fjernet');
    }

    setLoading(true);

    try {
      // Format the context URL if needed
      let finalContextUrl = contextUrl.trim();
      if (finalContextUrl && !finalContextUrl.match(/^https?:\/\//i)) {
        finalContextUrl = `https://${finalContextUrl}`;
      }
      
      console.log('[ProductionPage] Creating interview with context URL:', finalContextUrl);
      console.log('[ProductionPage] Sponsor data:', { isSponsored, sponsorCompany, sponsorLogoUrl, sponsorLogoPosition });
      
      // Create the interview directly in the interviews table
      const interview = {
        intervieweeName: name.trim(),
        title: title.trim(),
        company: company.trim() || '',
        context: context.trim(),
        contextUrl: finalContextUrl,
        contextImageUrl: contextImageUrl.trim() || undefined,
        contextImageCaption: contextImageCaption.trim() || undefined,
        question: question.trim(),
        answer: answer.trim(),
        tags: validTags,
        photoUrl: photoUrl,
        photographerCredit: photographerCredit.trim() || undefined,
        publication_status: 'pending',
        isSponsored: isSponsored,
        sponsorCompany: isSponsored ? sponsorCompany.trim() || undefined : undefined,
        sponsorLogoUrl: isSponsored ? sponsorLogoUrl.trim() || undefined : undefined,
        sponsorLogoPosition: isSponsored ? sponsorLogoPosition : 'profile'
      };

      // Use the existing interview service to create the interview
      const result = await InterviewManagementFacade.createInterview(interview);
      
      if (result.success) {
        toast.success('Intervju opprettet!');
        navigate('/interview-management');
      } else {
        toast.error(result.error || 'Kunne ikke opprette intervju');
      }
    } catch (err) {
      console.error('Feil ved opprettelse av intervju:', err);
      toast.error('En feil oppstod under opprettelse av intervju');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    answer,
    setAnswer,
    name,
    setName,
    title,
    setTitle,
    company,
    setCompany,
    context,
    setContext,
    contextUrl,
    setContextUrl,
    contextImageUrl,
    setContextImageUrl,
    contextImageCaption,
    setContextImageCaption,
    question,
    setQuestion,
    photoUrl,
    setPhotoUrl,
    photographerCredit,
    setPhotographerCredit,
    tags,
    setTags,
    isSponsored,
    setIsSponsored,
    sponsorCompany,
    setSponsorCompany,
    sponsorLogoUrl,
    setSponsorLogoUrl,
    sponsorLogoPosition,
    setSponsorLogoPosition,
    handleSubmit,
    VALID_TAGS
  };
};
